import { Carousel } from "antd";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./style.module.scss";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

export interface VLCarouselPaginationProps {
  /** false: no wrap around, prev / next are disabled on the first / last item */
  infinite?: boolean;
  /** more items can be loaded after the last one */
  hasMore?: boolean;
  /** more items are being loaded */
  loadingMore?: boolean;
  /** called when the current item gets close to the end and hasMore */
  onReachEnd?: () => void;
}

export interface VLCarouselProps extends VLCarouselPaginationProps {
  contentList: ReactElement[];
  afterChange?: (index: number) => void;
  totalText?: string;
  selectedItem?: number;
  totalCount?: number;
}

const MINIMUM_SLIDES_TO_SHOW = 6;
// load more items when the current one is this close to the end
const LOAD_MORE_THRESHOLD = MINIMUM_SLIDES_TO_SHOW;

export default function VLCarousel({
  contentList,
  afterChange,
  totalText,
  selectedItem = 0,
  totalCount,
  infinite = true,
  hasMore = false,
  loadingMore = false,
  onReachEnd,
}: VLCarouselProps) {
  const carouselRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(selectedItem);

  const slidesToShow =
    contentList.length > MINIMUM_SLIDES_TO_SHOW
      ? MINIMUM_SLIDES_TO_SHOW
      : contentList.length;

  const totalItems = totalCount || contentList.length;

  // Clamp selectedItem to available content for carousel navigation
  const clampedSelectedItem = Math.min(selectedItem, contentList.length - 1);

  /*
   * infinite: the current item is the carousel's centered slide.
   * not infinite: the strip is left aligned (no center mode, so no empty space before the first item) and the
   * current item is tracked here, since without center mode the carousel can't make one of the last items current.
   * The strip scrolls to keep the current item in view, near the middle when possible.
   */
  const scrollIndexFor = (index: number) =>
    Math.max(
      0,
      Math.min(
        index - Math.floor(slidesToShow / 2),
        contentList.length - slidesToShow,
      ),
    );

  useEffect(() => {
    wrapperRef.current?.focus();
    if (carouselRef.current && clampedSelectedItem !== currentSlide) {
      if (infinite) {
        carouselRef.current.goTo(clampedSelectedItem, false);
      }
      setCurrentSlide(selectedItem); // Keep original selectedItem for counter display
    }
  }, [selectedItem, currentSlide, clampedSelectedItem, infinite]);

  const scrollIndex = scrollIndexFor(currentSlide);
  useEffect(() => {
    if (!infinite) carouselRef.current?.goTo(scrollIndex);
  }, [infinite, scrollIndex]);

  const select = (index: number) => {
    setCurrentSlide(index);
    afterChange?.(index);
  };

  const isFirst = currentSlide <= 0;
  const isLast = currentSlide >= contentList.length - 1;
  const prevDisabled = !infinite && isFirst;
  const nextDisabled = !infinite && isLast && (!hasMore || loadingMore);
  const prev = () => {
    if (infinite) carouselRef.current?.prev();
    else if (!prevDisabled) select(currentSlide - 1);
  };
  const next = () => {
    if (infinite) carouselRef.current?.next();
    else if (!nextDisabled) select(currentSlide + 1);
  };

  useEffect(() => {
    if (
      hasMore &&
      !loadingMore &&
      currentSlide >= contentList.length - LOAD_MORE_THRESHOLD
    ) {
      onReachEnd?.();
    }
  }, [currentSlide, contentList.length, hasMore, loadingMore, onReachEnd]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.buttons}>
        <div className={styles.totalText}>
          {totalText
            ? `${totalText} ${totalItems}`
            : `Additional Items: ${totalItems}`}
        </div>
        <div className={styles.centerButtons}>
          <div
            className={classNames(styles.button, {
              [styles.disabled]: prevDisabled,
            })}
            onClick={prev}
            aria-disabled={prevDisabled}
            data-testid="carousel-prev"
          >
            <ArrowLeftOutlined />
          </div>
          <div>{`${currentSlide + 1} / ${totalItems}`}</div>
          <div
            className={classNames(styles.button, {
              [styles.disabled]: nextDisabled,
            })}
            onClick={next}
            aria-disabled={nextDisabled}
            data-testid="carousel-next"
          >
            {loadingMore && isLast ? (
              <LoadingOutlined data-testid="carousel-loading-more" />
            ) : (
              <ArrowRightOutlined />
            )}
          </div>
        </div>
      </div>

      <div
        ref={wrapperRef}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") prev();
          if (e.key === "ArrowRight") next();
        }}
      >
        <Carousel
          ref={carouselRef}
          initialSlide={
            infinite ? clampedSelectedItem : scrollIndexFor(clampedSelectedItem)
          }
          infinite={infinite}
          dots={false}
          arrows={false}
          slidesToShow={slidesToShow}
          lazyLoad={"ondemand"}
          slidesToScroll={1}
          centerMode={infinite}
          draggable={true}
          afterChange={(index) => {
            // not infinite: index is only the scroll position, not the current item
            if (infinite) select(index);
          }}
        >
          {contentList.map((content, index) => (
            <div
              key={index}
              className={classNames(styles.content, {
                [styles.current]: !infinite && index === currentSlide,
              })}
              data-testid="carousel-item"
              onClick={() =>
                infinite ? carouselRef.current?.goTo(index) : select(index)
              }
            >
              {content}
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
}
